// src/pages/admin/integrations/zoho/components/FlowsTab.tsx
import { useMemo, useState, type FC } from 'react';
import { useZohoFlows } from '../../../util/hooks/useZoho';

import FlowEditorModal from './FlowEditorModal';
import type { ToastFn, ZohoFlow } from '../../../types/integrations';
import { fmtDelay } from '../../../util/functions/UniversalFunctions';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}

function ToggleSwitch({ checked, onChange, label }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? 'bg-blue-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

interface FlowsTabProps {
  connected: boolean;
  toast: ToastFn;
}

const FlowsTab: FC<FlowsTabProps> = ({ connected, toast }) => {
  const { data, templates, saveFlow } = useZohoFlows();
  const [editing, setEditing] = useState<ZohoFlow | null>(null);

  const groups = useMemo<Array<[string, ZohoFlow[]]>>(() => {
    const g = new Map<string, ZohoFlow[]>();
    (data?.flows ?? []).forEach((f) => {
      if (!g.has(f.group)) g.set(f.group, []);
      g.get(f.group)!.push(f);
    });
    return Array.from(g.entries());
  }, [data]);

  const quickToggle = async (f: ZohoFlow, on: boolean) => {
    if (on && !f.templateName) {
      setEditing(f);
      return;
    }
    await saveFlow(f.key, { ...f, enabled: on });
    toast(on ? 'Automation on' : 'Automation off');
  };

  if (!data) return <div className="h-48 rounded-xl bg-slate-200 animate-pulse" />;

  return (
    <div className="flex flex-col gap-5">
      {!connected && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          Connect Zoho CRM first. You can set up the messages now; they start
          once connected.
        </div>
      )}

      {groups.map(([group, flows]) => (
        <section
          key={group}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        >
          <h2 className="px-4 py-3 font-semibold text-slate-900 border-b border-slate-200 bg-slate-50">
            {group}
          </h2>
          <ul className="divide-y divide-slate-100">
            {flows.map((f) => (
              <li key={f.key} className="flex items-center gap-3 px-4 py-3">
                <button
                  type="button"
                  className="flex-1 flex flex-col gap-0.5 text-left"
                  onClick={() => setEditing(f)}
                >
                  <strong className="text-sm text-slate-900">{f.label}</strong>
                  <span className="text-xs text-slate-500">
                    {f.trigger} · {fmtDelay(f.delayMinutes)}
                  </span>
                  <span className="text-xs">
                    {f.templateName ? (
                      <>
                        Template{' '}
                        <code className="bg-slate-100 px-1 rounded">
                          {f.templateName}
                        </code>
                      </>
                    ) : (
                      <span className="text-amber-600">No template chosen</span>
                    )}
                  </span>
                </button>
                <ToggleSwitch
                  checked={f.enabled}
                  label={`Turn ${f.label} ${f.enabled ? 'off' : 'on'}`}
                  onChange={(on) => void quickToggle(f, on)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {editing && (
        <FlowEditorModal
          flow={editing}
          templates={templates}
          variables={data.variables}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast('Saved');
          }}
          toast={toast}
        />
      )}
    </div>
  );
};

export default FlowsTab;